import React, { useEffect, useRef } from 'react';

import { useProductTourState, useTripsState } from '#states/hooks.js';

import { TourOverlay } from './TourOverlay.js';

export function ProductTour(): React.ReactNode {
  const tourState = useProductTourState();
  const tripsState = useTripsState();
  const hasStarted = useRef(false);

  // Auto-start tour when conditions are met (post-onboarding)
  useEffect(() => {
    if (hasStarted.current || tourState.isActive) return;
    if (!tourState.shouldShow) return;

    const timer = setTimeout(() => {
      if (tourState.shouldShow) {
        void tourState.start();
        hasStarted.current = true;
      }
    }, 800);

    return () => clearTimeout(timer);
  }, [tourState, tourState.shouldShow]);

  // When waiting for demo data, poll for demoDataReady on the trip document.
  // We poll because signal reactivity from Firestore updates doesn't reliably
  // trigger React re-renders through computed properties.
  useEffect(() => {
    if (!tourState.isWaitingForDemoData) return;

    const interval = setInterval(() => {
      const current = tripsState.current;
      if (current?.demoDataReady === true) {
        tourState.isWaitingForDemoData = false;
        tourState.isActive = true;
        tripsState.switchToPanel('chat');
      }
    }, 500);

    return () => clearInterval(interval);
  }, [tourState, tourState.isWaitingForDemoData, tripsState]);

  // Deactivate tour if user navigates away from the demo trip
  const currentTrip = tripsState.current;
  useEffect(() => {
    if (!tourState.isActive) return;
    if (!currentTrip || !currentTrip.isDemo) {
      tourState.isActive = false;
    }
  }, [tourState, currentTrip, currentTrip?.id]);

  if (!tourState.isActive) return null;

  return <TourOverlay tourState={tourState} />;
}
